import {
  query,
  collection,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
  Unsubscribe,
  getDoc,
} from "@firebase/firestore";
import { db } from "../config/firebase-config";
import { IScorecardMetadata } from "../models/ScorecardMetadata";

import AppStore from "../stores/AppStore";
import AppApi, { apiPathScorecardLevel } from "./AppApi";

const removeUndefinedFields = <T extends Record<string, unknown>>(item: T): T => {
  return Object.fromEntries(
    Object.entries(item).filter(([, value]) => value !== undefined)
  ) as T;
};

export default class IndividualScorecardApi {
  constructor(private api: AppApi, private store: AppStore) {}

  private getPath() {
    if (!this.store.scorecard.activeId) return null;
    return apiPathScorecardLevel(
      this.store.scorecard.activeId,
      "scorecardMetadata"
    );
  }

  private getPathByScorecardId(scorecardId: string) {
    return apiPathScorecardLevel(scorecardId, "scorecardMetadata");
  }

  async getByUid(id: string, notFound?: () => void) {
    const path = this.getPath();
    if (!path) return;

    const unsubscribe = onSnapshot(doc(db, path, id), (doc) => {
      if (!doc.exists) {
        if (notFound) notFound();
        return;
      }
      const item = { uid: doc.id, ...doc.data() } as IScorecardMetadata;
      this.store.individualScorecard.load([item]);
    });

    return unsubscribe;
  }

  // async getByUid(id: string) {
  //   const path = this.getPath();
  //   if (!path) return;

  //   const unsubscribe = onSnapshot(doc(db, path, id), (doc) => {
  //     if (!doc.exists) return;
  //     const item = { uid: doc.id, ...doc.data() } as IScorecardMetadata;

  //     this.store.individualScorecard.load([item]);
  //   });

  //   return unsubscribe;
  // }

  async getAll() {
    const path = this.getPath();
    if (!path) return;

    const $query = query(collection(db, path));

    // new promise
    return await new Promise<Unsubscribe>((resolve, reject) => {
      // on snapshot
      const unsubscribe = onSnapshot(
        $query,
        (querySnapshot) => {
          const items: IScorecardMetadata[] = [];
          querySnapshot.forEach((doc) => {
            items.push({ uid: doc.id, ...doc.data() } as IScorecardMetadata);
          });

          this.store.individualScorecard.load(items);
          resolve(unsubscribe);
        },
        // onError
        (error) => {
          reject();
        }
      );
    });
  }

  // create scorecardBatch
  async create(item: IScorecardMetadata) {
    const path = this.getPath();
    if (!path) return;

    // update in db
    try {
      await setDoc(doc(db, path, item.uid), item, {
        merge: true,
      });
      // update in store
      this.store.individualScorecard.load([item]);
    } catch (error) {
      // console.log(error);
    }
  }

  async getByUidFromScorecard(scorecardId: string, uid: string) {
    const path = this.getPathByScorecardId(scorecardId);
    const docSnap = await getDoc(doc(db, path, uid));

    if (!docSnap.exists()) return null;

    return { uid: docSnap.id, ...docSnap.data() } as IScorecardMetadata;
  }

  async duplicate(item: IScorecardMetadata, scorecardId: string) {
    const path = this.getPathByScorecardId(scorecardId);

    try {
      await setDoc(doc(db, path, item.uid), removeUndefinedFields({ ...item }), {
        merge: true,
      });
    } catch (error) {
      throw error;
    }
  }

  // update item
  async update(item: IScorecardMetadata) {
    const path = this.getPath();
    if (!path) return;

    // update in db
    try {
      await updateDoc(doc(db, path, item.uid), {
        ...item,
      });
      // update in store
      this.store.individualScorecard.load([item]);
    } catch (error) {
      // console.log(error);
    }
  }

  // delete scorecard batch
  async delete(item: IScorecardMetadata) {
    const path = this.getPath();
    if (!path) return;

    // remove from db
    try {
      await deleteDoc(doc(db, path, item.uid));
      // remove from store
      this.store.individualScorecard.remove(item.uid); // Remove from memory
    } catch (error) {
      // console.log(error);
    }
  }
}
